import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { append, clone } from 'ramda'
import { v4 as uuidv4 } from 'uuid'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  Class,
  Course,
  Quiz,
  ShowNotes,
  TextContent,
  TitleScreen,
  Video,
} from 'src/v3/core/types'

type ItemType =
  'title_screen' | 'video' | 'text_content' | 'quiz' | 'show_notes'

export type StreamStepVideo = Video & { videoUrl: string }

type Stream = {
  id: string
  classId: string
  items: Record<
    'titleScreen' | 'video' | 'textContent' | 'quiz' | 'showNotes',
    (TitleScreen | StreamStepVideo | TextContent | Quiz | ShowNotes) & {
      type: ItemType
    }
  >[]
}

type ItemStatus = 'started' | 'completed' | 'restarted'

export type ProgressItem = {
  id: string
  type: 'titleScreen' | 'video' | 'textContent' | 'quiz' | 'showNotes'
  status?: ItemStatus
  progress?: number
  classID: string
  userID: string
  videoID?: string
  textContentID?: string
  titleScreenID?: string
  quizID?: string
  showNotesID?: string
  createdTime?: string
  updatedTime?: string
}

type Progress = ProgressItem[]

type ClassStreamEvent = {
  id: string
  status: ItemStatus
  userID: string
  classID: string
  createdTime?: string
  updatedTime?: string
}

type CourseStreamEvent = {
  id: string
  status: ItemStatus
  userID: string
  courseID: string
  createdTime?: string
  cpdatedTime?: string
}

export type QuizQuestionSelection = {
  answerId: string
  questionId: string
  optionId?: string
}

export type QuizFinalResults = {
  score: number
  isPassing: boolean
}

type ProfileStatus = {
  invalidFields: string[]
}

export let fetchClasses = createAsyncThunk('classroom/fetchClassesStatus', () =>
  axios.get<Class[]>('/api/v2/classroom/classes').then(pluckData),
)

export let fetchCourses = createAsyncThunk('classroom/fetchCoursesStatus', () =>
  axios.get<Course[]>('/api/v2/classroom/courses').then(pluckData),
)

export let fetchStream = createAsyncThunk(
  'classroom/fetchStream',
  (id: string) =>
    axios.get<Stream>(`/api/v2/classroom/stream?classID=${id}`).then(pluckData),
)

export let fetchClassCompletionStatus = createAsyncThunk(
  'classroom/classCompletionStatus',
  (id: string) =>
    axios
      .get<ClassStreamEvent>(
        `/api/v2/classroom/class/completion-status?classID=${id}`,
      )
      .then(pluckData),
)

export let fetchCourseCompletionStatus = createAsyncThunk(
  'classroom/courseCompletionStatus',
  (id: string) =>
    axios
      .get<CourseStreamEvent>(
        `/api/v2/classroom/course/completion-status?courseID=${id}`,
      )
      .then(pluckData),
)

export let fetchProgress = createAsyncThunk(
  'classroom/fetchProgress',
  (id: string) =>
    axios
      .get<Progress>(`/api/v2/classroom/progress?classID=${id}`)
      .then(pluckData),
)

export let fetchQuiz = createAsyncThunk('classroom/fetchQuiz', (id: string) =>
  axios.get<Quiz>(`/api/v3/classroom/quiz?classID=${id}`).then(pluckData),
)

type CertificateStatus = 'noCertificate' | 'hasCertificate'
type CertificateAndStatus = {
  certificateID?: string
  certificateStatus: CertificateStatus
}

export let fetchCertificateAndStatus = createAsyncThunk(
  'classroom/fetchCertificateAndStatus',
  ({ courseID, userID }: { courseID: string; userID: string }) =>
    axios
      .get<CertificateAndStatus>(
        `/api/v2/classroom/certificate/status?courseID=${courseID}&userID=${userID}`,
      )
      .then(pluckData),
)

export let fetchCourseAccreditations = createAsyncThunk(
  'classroom/fetchCourseAccreditationsStatus',
  (courseID: string) =>
    axios
      .get<string[]>(
        `/api/v2/self-paced/classes/accreditations?courseID=${courseID}`,
      )
      .then(pluckData),
)

export let fetchProfileStatus = createAsyncThunk(
  'classroom/fetchProfileStatus',
  () =>
    axios
      .get<ProfileStatus>('/api/v2/classroom/user/validate')
      .then(pluckData)
      .then((data) => data?.invalidFields || []),
)

type ClassroomState = {
  courseID: string
  classID: string
  classesStatus: Status
  classes: Class[]
  coursesStatus: Status
  courses: Course[]
  classCompletionStatusStatus: Status
  classCompletionStatus: ClassStreamEvent
  courseCompletionStatusStatus: Status
  courseCompletionStatus: CourseStreamEvent
  certificateAndStatusStatus: Status
  certificateAndStatus: CertificateAndStatus
  streamStatus: Status
  stream: Stream
  progressStatus: Status
  progress: Progress
  quizStatus: Status
  quiz: Quiz
  courseAccreditationsStatus: Record<string, Status>
  courseAccreditations: Record<string, string[]>
  profileStatusStatus: Status
  profileStatus: string[]
  currentQuizQuestion: number
  currentQuizSelections: QuizQuestionSelection[]
  finalQuizResults?: QuizFinalResults
  quizSessionID: string
  currentStep: number
  videoProgresses: Record<string, number>
}

export let initialState: ClassroomState = {
  courseID: '',
  classID: '',
  classesStatus: 'uninitialized',
  classes: [],
  coursesStatus: 'uninitialized',
  courses: [],
  classCompletionStatusStatus: 'uninitialized',
  classCompletionStatus: {
    id: '',
    status: 'started',
    userID: '',
    classID: '',
  },
  courseCompletionStatusStatus: 'uninitialized',
  courseCompletionStatus: {
    id: '',
    status: 'started',
    userID: '',
    courseID: '',
  },
  certificateAndStatusStatus: 'uninitialized',
  certificateAndStatus: {
    certificateStatus: 'noCertificate',
  },
  streamStatus: 'uninitialized',
  stream: {
    id: '',
    classId: '',
    items: [],
  },
  quizStatus: 'uninitialized',
  quiz: {
    id: '',
    classID: '',
    name: '',
    description: '',
    passingScore: 0,
    questions: [],
  },
  courseAccreditationsStatus: {},
  courseAccreditations: {},
  profileStatusStatus: 'uninitialized',
  profileStatus: [],
  currentQuizQuestion: 0,
  currentQuizSelections: [],
  quizSessionID: uuidv4(),
  finalQuizResults: undefined,
  progressStatus: 'uninitialized',
  progress: [],
  currentStep: 0,
  videoProgresses: {},
}

export let classroomSlice = createSlice({
  name: 'classroom',
  initialState,
  reducers: {
    courseIDChanged: (state, action: PayloadAction<string>) => {
      state.courseID = action.payload
    },
    classIDChanged: (state, action: PayloadAction<string>) => {
      state.classID = action.payload
    },
    coursesChanged: (state, action: PayloadAction<Course[]>) => {
      state.courses = action.payload
    },
    classesChanged: (state, action: PayloadAction<Class[]>) => {
      state.classes = action.payload
    },
    classCompletionStatusStatusChanged: (
      state,
      action: PayloadAction<Status>,
    ) => {
      state.classCompletionStatusStatus = action.payload
    },
    classCompletionStatusChanged: (
      state,
      action: PayloadAction<ClassStreamEvent>,
    ) => {
      state.classCompletionStatus = action.payload
    },
    courseCompletionStatusStatusChanged: (
      state,
      action: PayloadAction<Status>,
    ) => {
      state.courseCompletionStatusStatus = action.payload
    },
    courseCompletionStatusChanged: (
      state,
      action: PayloadAction<CourseStreamEvent>,
    ) => {
      state.courseCompletionStatus = action.payload
    },
    streamChanged: (state, action: PayloadAction<Stream>) => {
      state.stream = action.payload
    },
    proggresChanged: (state, action: PayloadAction<Progress>) => {
      state.progress = action.payload
    },
    progressResetted: (state) => {
      state.classID = initialState.classID
      state.courseID = initialState.courseID
      state.progress = initialState.progress
      state.progressStatus = initialState.progressStatus
      state.stream = initialState.stream
      state.streamStatus = initialState.streamStatus
      state.classCompletionStatus = initialState.classCompletionStatus
      state.classCompletionStatusStatus =
        initialState.classCompletionStatusStatus
      state.courseCompletionStatusStatus =
        initialState.courseCompletionStatusStatus
      state.courseCompletionStatus = initialState.courseCompletionStatus
      state.certificateAndStatusStatus = initialState.certificateAndStatusStatus
      state.certificateAndStatus = initialState.certificateAndStatus
      state.quiz = initialState.quiz
      state.quizStatus = initialState.quizStatus
      state.currentQuizQuestion = initialState.currentQuizQuestion
      state.currentQuizSelections = initialState.currentQuizSelections
      state.quizSessionID = uuidv4()
      state.finalQuizResults = initialState.finalQuizResults
      state.certificateAndStatus = initialState.certificateAndStatus
      state.currentStep = initialState.currentStep
      state.videoProgresses = initialState.videoProgresses
    },
    quizChanged: (state, action: PayloadAction<Quiz>) => {
      state.quiz = action.payload
    },
    quizSessionStarted: (state) => {
      state.quizSessionID = uuidv4()
    },
    currentQuizQuestionSteppedForward: (state) => {
      state.currentQuizQuestion =
        state.currentQuizQuestion < state.quiz.questions.length
          ? state.currentQuizQuestion + 1
          : state.currentQuizQuestion
    },
    quizQuestionSelectionAdded: (
      state,
      action: PayloadAction<QuizQuestionSelection>,
    ) => {
      state.currentQuizSelections = append(
        action.payload,
        state.currentQuizSelections,
      )
    },
    clearQuizSelections: (state) => {
      state.currentQuizSelections = []
    },
    quizRestarted: (state) => {
      state.currentQuizQuestion = initialState.currentQuizQuestion
      state.currentQuizSelections = initialState.currentQuizSelections
      state.quizSessionID = uuidv4()
      state.finalQuizResults = initialState.finalQuizResults
    },
    quizFinalResultsChanged: (
      state,
      action: PayloadAction<QuizFinalResults>,
    ) => {
      state.finalQuizResults = action.payload
    },
    currentStepChanged: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload
    },
    steppedBackward: (state) => {
      state.currentStep =
        state.currentStep > 0 ? state.currentStep - 1 : state.currentStep
    },
    steppedForward: (state) => {
      state.currentStep =
        state.currentStep < state.progress.length - 1
          ? state.currentStep + 1
          : state.currentStep
    },
    stepCompleted: (state) => {
      let step = {
        ...clone(state.progress[state.currentStep]),
        status: 'completed' as ItemStatus,
      }
      state.progress[state.currentStep] = step
    },
    fetchCertificateAndStatusChanged: (state, action) => {
      state.certificateAndStatus = action.payload
    },
    videoProgressMade: (
      state,
      action: PayloadAction<{ id: string; progress: number }>,
    ) => {
      state.videoProgresses[action.payload.id] = action.payload.progress
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'fulfilled'
        state.courses = action.payload
      })
      .addCase(fetchClasses.pending, (state) => {
        state.classesStatus = 'pending'
      })
      .addCase(fetchClasses.rejected, (state) => {
        state.classesStatus = 'rejected'
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        state.classesStatus = 'fulfilled'
        state.classes = action.payload
      })
      .addCase(fetchCourseCompletionStatus.pending, (state) => {
        state.courseCompletionStatusStatus = 'pending'
      })
      .addCase(fetchCourseCompletionStatus.rejected, (state) => {
        state.courseCompletionStatusStatus = 'rejected'
      })
      .addCase(fetchCourseCompletionStatus.fulfilled, (state, action) => {
        state.courseCompletionStatusStatus = 'fulfilled'
        state.courseCompletionStatus = action.payload
      })
      .addCase(fetchClassCompletionStatus.pending, (state) => {
        state.classCompletionStatusStatus = 'pending'
      })
      .addCase(fetchClassCompletionStatus.rejected, (state) => {
        state.classCompletionStatusStatus = 'rejected'
      })
      .addCase(fetchClassCompletionStatus.fulfilled, (state, action) => {
        state.classCompletionStatusStatus = 'fulfilled'
        state.classCompletionStatus = action.payload
      })
      .addCase(fetchCertificateAndStatus.pending, (state) => {
        state.certificateAndStatusStatus = 'pending'
      })
      .addCase(fetchCertificateAndStatus.rejected, (state) => {
        state.certificateAndStatusStatus = 'rejected'
      })
      .addCase(fetchCertificateAndStatus.fulfilled, (state, action) => {
        state.certificateAndStatusStatus = 'fulfilled'
        state.certificateAndStatus = action.payload
      })
      .addCase(fetchStream.pending, (state) => {
        state.streamStatus = 'pending'
      })
      .addCase(fetchStream.rejected, (state) => {
        state.streamStatus = 'rejected'
      })
      .addCase(fetchStream.fulfilled, (state, action) => {
        state.streamStatus = 'fulfilled'
        state.stream = action.payload
      })
      .addCase(fetchQuiz.pending, (state) => {
        state.quizStatus = 'pending'
      })
      .addCase(fetchQuiz.rejected, (state) => {
        state.quizStatus = 'rejected'
      })
      .addCase(fetchQuiz.fulfilled, (state, action) => {
        state.quizStatus = 'fulfilled'
        state.quiz = action.payload
      })
      .addCase(fetchCourseAccreditations.pending, (state, action) => {
        state.courseAccreditationsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseAccreditations.rejected, (state, action) => {
        state.courseAccreditationsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseAccreditations.fulfilled, (state, action) => {
        state.courseAccreditationsStatus[action.meta.arg] = 'fulfilled'
        state.courseAccreditations[action.meta.arg] = action.payload
      })
      .addCase(fetchProfileStatus.pending, (state) => {
        state.profileStatusStatus = 'pending'
      })
      .addCase(fetchProfileStatus.rejected, (state) => {
        state.profileStatusStatus = 'rejected'
      })
      .addCase(fetchProfileStatus.fulfilled, (state, action) => {
        state.profileStatusStatus = 'fulfilled'
        state.profileStatus = action.payload
      })
      .addCase(fetchProgress.pending, (state) => {
        state.progressStatus = 'pending'
      })
      .addCase(fetchProgress.rejected, (state) => {
        state.progressStatus = 'rejected'
      })
      .addCase(fetchProgress.fulfilled, (state, action) => {
        state.progressStatus = 'fulfilled'
        state.progress = action.payload
        let nextIndex = action.payload.findIndex(
          (x) => x.status !== 'completed',
        )
        state.currentStep = nextIndex === 1 ? 0 : nextIndex
      })
  },
})

export let {
  courseIDChanged,
  classIDChanged,
  classesChanged,
  coursesChanged,
  classCompletionStatusStatusChanged,
  classCompletionStatusChanged,
  courseCompletionStatusStatusChanged,
  courseCompletionStatusChanged,
  fetchCertificateAndStatusChanged,
  streamChanged,
  proggresChanged,
  progressResetted,
  quizChanged,
  currentQuizQuestionSteppedForward,
  quizQuestionSelectionAdded,
  currentStepChanged,
  quizRestarted,
  steppedBackward,
  steppedForward,
  stepCompleted,
  clearQuizSelections,
  quizFinalResultsChanged,
  videoProgressMade,
} = classroomSlice.actions

export let selectCourseID = (state: AppRootState) => state.classroom.courseID
export let selectClassID = (state: AppRootState) => state.classroom.classID

export let selectCourses = (state: AppRootState) => state.classroom.courses
export let selectCoursesStatus = (state: AppRootState) =>
  state.classroom.coursesStatus
export let selectCourseByID = (id: string) => (state: AppRootState) =>
  state.classroom.courses.find((c) => c.id === id)

export let selectClasses = (state: AppRootState) => state.classroom.classes
export let selectClassesStatus = (state: AppRootState) =>
  state.classroom.classesStatus
export let selectClassByID = (id: string) => (state: AppRootState) =>
  state.classroom.classes.find((c) => c.id === id)
export let selectClassesByCourseID = (id: string) => (state: AppRootState) =>
  state.classroom.classes.filter((c) => c.courseID === id)

export let selectClassCompletionStatus = (state: AppRootState) =>
  state.classroom.classCompletionStatus
export let selectClassCompletionStatusStatus = (state: AppRootState) =>
  state.classroom.classCompletionStatusStatus

export let selectCourseCompletionStatus = (state: AppRootState) =>
  state.classroom.courseCompletionStatus
export let selectCourseCompletionStatusStatus = (state: AppRootState) =>
  state.classroom.courseCompletionStatusStatus

export let selectCertificateAndStatus = (state: AppRootState) =>
  state.classroom.certificateAndStatus
export let selectCertificateAndStatusStatus = (state: AppRootState) =>
  state.classroom.certificateAndStatusStatus

export let selectStream = (state: AppRootState) => state.classroom.stream
export let selectStreamStatus = (state: AppRootState) =>
  state.classroom.streamStatus
export let selectStreamItems = (state: AppRootState) =>
  state.classroom.stream?.items.map((x) => Object.values(x)[0])

export let selectQuiz = (state: AppRootState) => state.classroom.quiz
export let selectQuizStatus = (state: AppRootState) =>
  state.classroom.quizStatus

export let selectCourseAccreditationsByID =
  (id: string) => (state: AppRootState) =>
    state.classroom.courseAccreditations[id]
export let selectCourseAccreditationsStatus = (state: AppRootState) =>
  state.classroom.courseAccreditationsStatus

export let selectProfileStatus = (state: AppRootState) =>
  state.classroom.profileStatus
export let selectProfileStatusStatus = (state: AppRootState) =>
  state.classroom.profileStatusStatus

export let selectCurrentQuizQuestion = (state: AppRootState) =>
  state.classroom.currentQuizQuestion
export let selectCurrentQuizQuestionAtIndex =
  (idx: number) => (state: AppRootState) =>
    state.classroom.quiz.questions[idx]

export let selectCurrentQuizStep = (state: AppRootState) =>
  state.classroom.currentQuizQuestion
export let selectCurrentQuizSelections = (state: AppRootState) =>
  state.classroom.currentQuizSelections
export let selectQuizSessionID = (state: AppRootState) =>
  state.classroom.quizSessionID
export let selectQuizFinalResults = (state: AppRootState) =>
  state.classroom.finalQuizResults

export let selectProgress = (state: AppRootState) => state.classroom.progress
export let selectProgressStatus = (state: AppRootState) =>
  state.classroom.progressStatus
export let selectProgressItem = (idx: number) => (state: AppRootState) =>
  state.classroom.progress[idx]

export let selectCurrentStep = (state: AppRootState) =>
  state.classroom.currentStep

export let selectVideoProgress = (id: string) => (state: AppRootState) =>
  state.classroom.videoProgresses[id]

export let validateProfileOnChange =
  // eslint-disable-next-line consistent-return
  (store: any) => (next: any) => (action: any) => {
    if (action.type === 'profile/fetchUserStatus/fulfilled') {
      store.dispatch(fetchProfileStatus())
    }

    if (action) {
      return next(action)
    }
  }

export default classroomSlice.reducer
